<template>
  <div class="translatable-input">
    <!-- Single language mode -->
    <div v-if="!isMultiLanguage">
      <label v-if="label">{{ label }} <span v-if="required" class="text-danger">*</span></label>
      <input
        v-if="type === 'text'"
        type="text"
        class="form-control"
        :value="defaultValue"
        :placeholder="placeholder"
        :required="required"
        :disabled="disabled"
        @input="$emit('input', $event.target.value)"
      />
      <textarea
        v-else-if="type === 'textarea'"
        class="form-control"
        :value="defaultValue"
        :placeholder="placeholder"
        :rows="rows"
        :required="required"
        :disabled="disabled"
        @input="$emit('input', $event.target.value)"
      ></textarea>
      <editor
        v-else-if="type === 'editor'"
        :editor="editorInstance"
        :value="defaultValue"
        :config="editorConfig"
        @input="$emit('input', $event)"
      />
      <select
        v-else-if="type === 'select'"
        class="form-control"
        :value="defaultValue"
        :required="required"
        :disabled="disabled"
        @change="$emit('input', $event.target.value)"
      >
        <option value="">Selecione uma opção</option>
        <option v-for="opt in getOptionsForLanguage('default')" :key="opt" :value="opt">{{ opt }}</option>
      </select>
    </div>

    <!-- Multi language mode -->
    <div v-else>
      <label v-if="label">
        {{ label }}
        <span v-if="required" class="text-danger">*</span>
        <span class="badge badge-info ml-2">Multi-idioma</span>
      </label>

      <b-tabs content-class="mt-2" nav-class="nav-tabs-custom">
        <b-tab
          v-for="lang in activeLanguages"
          :key="lang.id"
          :title="getTabTitle(lang)"
          :active="lang.is_default"
        >
          <input
            v-if="type === 'text'"
            type="text"
            class="form-control"
            :value="getTranslationValue(lang.id)"
            :placeholder="placeholder || `${label} em ${lang.native_name}`"
            :required="required && lang.is_default"
            :disabled="disabled"
            @input="setTranslationValue(lang.id, $event.target.value)"
          />
          <textarea
            v-else-if="type === 'textarea'"
            class="form-control"
            :value="getTranslationValue(lang.id)"
            :placeholder="placeholder || `${label} em ${lang.native_name}`"
            :rows="rows"
            :required="required && lang.is_default"
            :disabled="disabled"
            @input="setTranslationValue(lang.id, $event.target.value)"
          ></textarea>
          <editor
            v-else-if="type === 'editor'"
            :editor="editorInstance"
            :value="getTranslationValue(lang.id)"
            :config="editorConfig"
            @input="setTranslationValue(lang.id, $event)"
          />
          <select
            v-else-if="type === 'select'"
            class="form-control"
            :value="getTranslationValue(lang.id)"
            :required="required && lang.is_default"
            :disabled="disabled"
            @change="setTranslationValue(lang.id, $event.target.value)"
          >
            <option value="">Selecione uma opção</option>
            <option v-for="opt in getOptionsForLanguage(lang.code)" :key="opt" :value="opt">{{ opt }}</option>
          </select>
        </b-tab>
      </b-tabs>
    </div>
  </div>
</template>

<script>
import CKEditor from '@ckeditor/ckeditor5-vue'
import FullEditor from 'ckeditor5-build-full'

export default {
  name: 'TranslatableInput',
  components: {
    editor: CKEditor.component
  },
  props: {
    // Basic input props (Vue 2 uses 'value' for v-model)
    value: {
      type: [String, Object],
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    type: {
      type: String,
      default: 'text', // text, textarea, editor, select
      validator: (value) => ['text', 'textarea', 'editor', 'select'].includes(value)
    },
    rows: {
      type: Number,
      default: 3
    },
    options: {
      type: [Array, Object],
      default: () => []
    },
    required: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    // Translation props
    field: {
      type: String,
      required: true
    },
    translations: {
      type: Object,
      default: () => ({})
    },
    activeLanguages: {
      type: Array,
      default: () => []
    },
    isMultiLanguage: {
      type: Boolean,
      default: false
    },
    defaultLanguageId: {
      type: Number,
      default: null
    }
  },
  data () {
    return {
      editorInstance: FullEditor,
      editorConfig: {}
    }
  },
  computed: {
    defaultValue () {
      // For single language mode, use value (Vue 2 v-model)
      if (typeof this.value === 'string') {
        return this.value
      }
      // If object, get the default language value
      if (this.defaultLanguageId && this.translations[this.defaultLanguageId]) {
        return this.translations[this.defaultLanguageId][this.field] || ''
      }
      return ''
    }
  },
  methods: {
    getTabTitle (lang) {
      // Safely build tab title from language properties
      const flag = lang.flag || ''
      const name = lang.name || lang.native_name || lang.code || ''
      return flag ? `${flag} ${name}` : name
    },
    getTranslationValue (languageId) {
      if (this.translations[languageId] && this.translations[languageId][this.field] !== undefined) {
        return this.translations[languageId][this.field]
      }
      // Fallback to value for default language (Vue 2 v-model)
      if (languageId === this.defaultLanguageId && typeof this.value === 'string') {
        return this.value
      }
      return ''
    },
    setTranslationValue (languageId, value) {
      // Create a deep copy to ensure Vue reactivity works properly
      const updatedTranslations = JSON.parse(JSON.stringify(this.translations))

      if (!updatedTranslations[languageId]) {
        updatedTranslations[languageId] = {}
      }
      updatedTranslations[languageId][this.field] = value

      this.$emit('update:translations', updatedTranslations)

      // Also emit input for the default language (Vue 2 v-model)
      if (languageId === this.defaultLanguageId) {
        this.$emit('input', value)
      }
    },
    getOptionsForLanguage (langCode) {
      // If options is an array, return it directly
      if (Array.isArray(this.options)) {
        return this.options
      }
      // If options is an object keyed by language code, get the right one
      if (this.options && typeof this.options === 'object') {
        // Try exact match first (e.g., 'pt-BR')
        if (this.options[langCode]) {
          return this.options[langCode]
        }
        // Try prefix match (e.g., 'pt' for 'pt-BR')
        const prefix = langCode ? langCode.split('-')[0] : ''
        if (prefix && this.options[prefix]) {
          return this.options[prefix]
        }
        // Fallback to default or first available
        if (this.options['default']) {
          return this.options['default']
        }
        const keys = Object.keys(this.options)
        if (keys.length > 0) {
          return this.options[keys[0]]
        }
      }
      return []
    }
  }
}
</script>

<style scoped>
.translatable-input {
  margin-bottom: 1rem;
}
.badge-info {
  font-size: 0.7rem;
  font-weight: normal;
}
</style>
